import { ComponentType, FC, useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  ChallengesService,
  Challenge
} from "../sdk"
import PageLoader from "../components/PageLoader"
import useError from "../hooks/useError"
import ChallengeGeneral from "../components/challenge/ChallengeGeneral"
import classNames from "classnames"
import ChallengeTasks from "../components/challenge/ChallengeTasks"
import { useChallenge } from "../stores/challenge"
import ChallengeNotifications from "../components/challenge/ChallengeNotifications"
import Tag from "../components/Tag"
import ChallengeSubscriptions from "../components/challenge/ChallengeSubscriptions"
import ChallengeRedeemCodes from "../components/challenge/ChallengeRedeemCodes"
import Message from "../components/Message"
import Button from "../components/Button"
import ChallengeDescriptionEditor from "../components/challenge/ChallengeDescriptionEditor"
import ChallengeSubmissions from "../components/challenge/ChallengeSubmissions"
import { useSubmissions } from "../stores/submissions"

type SectionId = "general" | "editor" | "tasks" | "notifications" | "users-subscriptions" | "teams-subscriptions" | "redeem-codes" | "submissions"

export type ChallengeSectionProps = {
  challenge: Challenge
}
type Section = {
  id: SectionId
  label: string
  component: ComponentType<ChallengeSectionProps>
}
type SectionsMap = {
  [key in SectionId]: Section
}

const sections: Section[] = [
  {
    id: "general",
    label: "Generale",
    component: ChallengeGeneral
  },
  {
    id: "editor",
    label: "Editor HTML",
    component: ChallengeDescriptionEditor
  },
  {
    id: "tasks",
    label: "Tasks",
    component: ChallengeTasks
  },
  {
    id: "notifications",
    label: "Notifiche live",
    component: ChallengeNotifications
  },
  {
    id: "users-subscriptions",
    label: "Utenti iscritti",
    component: ChallengeSubscriptions
  },
  {
    id: "teams-subscriptions",
    label: "Squadre iscritte",
    component: ChallengeSubscriptions
  },
  {
    id: "submissions",
    label: "Submissions",
    component: ChallengeSubmissions
  },
  {
    id: "redeem-codes",
    label: "Redeem Codes",
    component: ChallengeRedeemCodes
  },
  {
    id: "teams-subscriptions",
    label: "Squadre iscritte",
    component: ChallengeSubscriptions
  }
]
const sectionsMap = sections.reduce((sections: SectionsMap, section: Section) => {
  sections[section.id] = section
  return sections
}, {} as SectionsMap)

const ChallengePage: FC = () => {
  const challenge = useChallenge((state) => state.challenge)
  const setChallenge = useChallenge((state) => state.setChallenge)
  const reset = useChallenge((state) => state.reset)
  const submissionsReset = useSubmissions(state => state.reset)

  const [loading, setLoading] = useState<boolean>(true)
  const [activeSection, setActiveSection] = useState<SectionId>("general")

  const Section = sectionsMap[activeSection].component

  const { id } = useParams()
  const { handleError } = useError()

  const getChallenge = async(): Promise<void> => {
    try {
      const { data: challenge } = await ChallengesService.getChallenge({
        handler: {
          challengeId: Number(id)
        }
      })
      setChallenge(challenge)
    } catch (err) {
      handleError(err)
    }
    setLoading(false)
  }

  useEffect(() => {
    void getChallenge()

    return (): void => {
      reset()
      submissionsReset()
    }
  }, [])

  const tabHidden = (id: SectionId): boolean => {
    return id === "users-subscriptions" && !challenge?.individual || id === "teams-subscriptions" && challenge?.individual || id === "redeem-codes" && !challenge?.private || false
  }

  return (
    <>
      {loading ?
        <PageLoader className="h-screen w-screen top-0 left-0 fixed"/>
        :
        <div>
          {challenge ?
            <>
              <div className="flex items-center gap-x-4 mb-10 ml-3">
                <h1 className="text-4xl font-bold">{challenge.name}</h1>

                {challenge.training &&
                  <Tag>Allenamento</Tag>
                }
              </div>

              <ul className="tabs__nav">
                {sections.map((section) => (
                  <li
                    key={section.id}
                    className={classNames("tabs__item", tabHidden(section.id) && "hidden", section.id === activeSection && "tabs__item--active")}
                    onClick={(): void => setActiveSection(section.id)}
                  >
                    {section.label}
                  </li>
                ))}
              </ul>

              <div className="tabs__section">
                <Section challenge={challenge} />
              </div>
            </>
            :
            <Message title="Gara non trovata" icon="database-off">
              <Link to="/challenges">
                <Button>Torna alle gare</Button>
              </Link>
            </Message>
          }
        </div>
      }
    </>
  )
}

export default ChallengePage